<div class="services-page">

  <!-- ── Page header ── -->
  <div class="page-header">
    <div class="page-header-left">
      <button mat-icon-button (click)="goBack()" matTooltip="Back to dashboard">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <div>
        <h1 class="page-title">My Services</h1>
        <p class="page-subtitle">Services you offer appear in Browse Services for customers to find and book.</p>
      </div>
    </div>
    <button mat-raised-button color="primary" (click)="openAddForm()" *ngIf="!showForm">
      <mat-icon>add</mat-icon> Add Service
    </button>
  </div>

  <!-- ── Add / Edit form ── -->
  <div class="form-card" *ngIf="showForm">
    <h2 class="form-title">{{ editingId ? 'Edit Service' : 'New Service' }}</h2>

    <form [formGroup]="serviceForm" (ngSubmit)="onSubmit()" class="service-form">
      <div class="form-row two-col">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Service Title</mat-label>
          <input matInput formControlName="title" placeholder="e.g. Whole-House Electrical Inspection" />
          <mat-error>Title is required</mat-error>
        </mat-form-field>

        <!-- AI title suggestions panel -->
        <div class="suggestions-panel" *ngIf="isFetchingSuggestions || titleSuggestions || suggestionError">
          <div class="suggestions-header">
            <span class="suggestions-badge">
              <mat-icon>auto_awesome</mat-icon> Paul &amp; Sons Operations
            </span>
            <button type="button" mat-icon-button class="suggestions-dismiss"
                    (click)="dismissSuggestions()" *ngIf="!isFetchingSuggestions">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <!-- Loading -->
          <div class="suggestions-loading" *ngIf="isFetchingSuggestions">
            <div class="css-spinner suggestions-spinner"></div>
            <span>Analyzing title &amp; generating suggestions…</span>
          </div>

          <!-- Error -->
          <div class="suggestions-error" *ngIf="suggestionError && !isFetchingSuggestions">
            <mat-icon>wifi_off</mat-icon>
            Could not reach Paul &amp; Sons Operations service.
          </div>

          <!-- Suggestions -->
          <ng-container *ngIf="titleSuggestions && !isFetchingSuggestions">
            <p class="suggestions-label">Suggested titles — click to apply:</p>
            <div class="suggestions-chips">
              <button type="button" class="suggestion-chip"
                      *ngFor="let s of titleSuggestions.suggestions"
                      (click)="applySuggestion(s)">
                {{ s }}
              </button>
            </div>

            <div class="suggestions-tips" *ngIf="titleSuggestions.tips?.length">
              <p class="tips-label"><mat-icon>lightbulb</mat-icon> Tips</p>
              <ul>
                <li *ngFor="let t of titleSuggestions.tips">{{ t }}</li>
              </ul>
            </div>
          </ng-container>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Category</mat-label>
          <mat-select formControlName="category">
            <mat-option *ngFor="let cat of categories" [value]="cat">{{ cat }}</mat-option>
          </mat-select>
          <mat-error>Category is required</mat-error>
        </mat-form-field>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <textarea matInput formControlName="description" rows="3"
                    placeholder="Describe what this service includes…"></textarea>
        </mat-form-field>
      </div>

      <div class="form-row three-col">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Price</mat-label>
          <span matPrefix>$&nbsp;</span>
          <input matInput type="number" formControlName="price" min="0" step="0.01" />
          <mat-error>Valid price required</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Duration (minutes)</mat-label>
          <input matInput type="number" formControlName="duration" min="1" />
          <mat-error>Duration required</mat-error>
        </mat-form-field>

        <div class="recurring-toggle">
          <mat-slide-toggle formControlName="isRecurring" color="primary">
            Recurring Service
          </mat-slide-toggle>
          <span class="toggle-hint">Customers can schedule this repeatedly</span>
        </div>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Service Area</mat-label>
          <input matInput formControlName="serviceArea"
                 placeholder="e.g. Boston, MA; Cambridge, MA" />
          <mat-hint>Comma-separated cities or regions</mat-hint>
          <mat-error>Service area is required</mat-error>
        </mat-form-field>
      </div>

      <div class="form-actions">
        <button type="button" mat-stroked-button (click)="cancelEdit()" [disabled]="isSaving">
          Cancel
        </button>
        <button type="submit" mat-raised-button color="primary"
                [disabled]="serviceForm.invalid || isSaving">
          <mat-icon>save</mat-icon>
          {{ isSaving ? 'Saving…' : (editingId ? 'Update Service' : 'Add Service') }}
        </button>
      </div>
    </form>
  </div>

  <!-- ── Loading ── -->
  <div class="loading-state" *ngIf="isLoading">
    <div class="css-spinner"></div>
    <span>Loading services…</span>
  </div>

  <!-- ── Empty ── -->
  <div class="empty-state" *ngIf="!isLoading && services.length === 0 && !showForm">
    <mat-icon>home_repair_service</mat-icon>
    <h3>No services yet</h3>
    <p>Add your first service to start getting discovered by customers.</p>
    <button mat-raised-button color="primary" (click)="openAddForm()">
      <mat-icon>add</mat-icon> Add Your First Service
    </button>
  </div>

  <!-- ── Services list ── -->
  <div class="services-list" *ngIf="!isLoading && services.length > 0">
    <div class="service-row" *ngFor="let svc of services">
      <div class="service-row-body">
        <div class="service-category-tag">{{ svc.category }}</div>
        <div class="service-row-title">{{ svc.title }}</div>
        <div class="service-row-desc" *ngIf="svc.description">{{ svc.description }}</div>
        <div class="service-row-meta">
          <span><mat-icon class="row-meta-icon">attach_money</mat-icon>{{ svc.price | currency }}</span>
          <span><mat-icon class="row-meta-icon">schedule</mat-icon>{{ svc.duration }} min</span>
          <span *ngIf="svc.isRecurring"><mat-icon class="row-meta-icon">repeat</mat-icon>Recurring</span>
          <span *ngIf="svc.serviceArea?.length"><mat-icon class="row-meta-icon">location_on</mat-icon>{{ svc.serviceArea.join(', ') }}</span>
        </div>
      </div>
      <div class="service-row-actions">
        <button mat-icon-button color="primary" (click)="editService(svc)" matTooltip="Edit">
          <mat-icon>edit</mat-icon>
        </button>
        <button mat-icon-button color="warn" (click)="deleteService(svc)" matTooltip="Delete">
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    </div>
  </div>

</div>
